import { useState, useCallback } from "react";
import html2canvas from "html2canvas";
import { DateRange } from "@/components/Metrics/DateRangeSelector";

interface UseMetricsDownloadReturn {
  downloadMetrics: () => Promise<void>;
  isDownloading: boolean;
  error: string | null;
}

export const useMetricsDownload = (
  dateRange: DateRange,
  shopName?: string
): UseMetricsDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sanitizeFileName = (text: string): string => {
    return text.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  };

  const formatDateRange = (range: DateRange): string => {
    const start = range.start.toISOString().split("T")[0];
    const end = range.end.toISOString().split("T")[0];
    return `${start}-to-${end}`;
  };

  const generateFileName = (): string => {
    const timestamp = new Date().toISOString().split("T")[0];
    const sanitizedShopName = shopName ? sanitizeFileName(shopName) : "shop";
    const sanitizedDateRange = sanitizeFileName(formatDateRange(dateRange));
    return `metrics-${sanitizedShopName}-${sanitizedDateRange}-${timestamp}.png`;
  };

  const downloadMetrics = useCallback(async (): Promise<void> => {
    setIsDownloading(true);
    setError(null);

    try {
      // Find the main metrics container (exclude header and navigation)
      const metricsContainer = document.querySelector(
        ".space-y-6.p-6"
      ) as HTMLElement;

      if (!metricsContainer) {
        throw new Error("Metrics container not found");
      }

      // Wait a bit to ensure all charts and content are fully rendered
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Generate canvas from the metrics container
      const canvas = await html2canvas(metricsContainer, {
        useCORS: true,
        allowTaint: true,
        width: metricsContainer.scrollWidth,
        height: metricsContainer.scrollHeight,
        background: "#202124",
      });

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            throw new Error("Failed to generate image blob");
          }

          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = generateFileName();

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up
          URL.revokeObjectURL(url);
        },
        "image/png",
        0.95
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download metrics";
      setError(errorMessage);
      console.error("Metrics download error:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [dateRange, shopName]);

  return {
    downloadMetrics,
    isDownloading,
    error,
  };
};
